import * as React from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import BrowserUpdatedIcon from '@mui/icons-material/BrowserUpdated';
import FilterListIcon from '@mui/icons-material/FilterList';
import { visuallyHidden } from '@mui/utils';
import io from "socket.io-client";
import { useAuthContext } from '../hooks/useAuthContext';

const headCells = [
  {
    id: 'clientId',
    numeric: false,
    disablePadding: true,
    label: 'Client ID)',
  },
  {
    id: 'clientName',
    numeric: false,
    disablePadding: true,
    label: 'Client Name)',
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: 'Status',
  },
  {
    id: 'version',
    numeric: false,
    disablePadding: true,
    label: 'Version',
  },
  {
    id: 'updatedAt',
    numeric: false,
    disablePadding: false,
    label: 'Last Updated',
  },
  {
    id: 'createdAt',
    numeric: false,
    disablePadding: false,
    label: 'Created At',
  },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function EnhancedTableHead(props) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
    props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'select all desserts',
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  const { numSelected } = props; // numSelected 是一個陣列 (包含 clientIds)
  const { user } = useAuthContext();

  // to post /api/v1/device/upgrade/:clientId for each selected device
  const handleUpgradeFirmware = async () => {
    if (numSelected.length === 0) {
      alert('Please select at least one device to upgrade.');
      return;
    }

    // 顯示確認對話框
    const confirmUpgrade = window.confirm(
      `Are you sure you want to upgrade firmware for ${numSelected.length} device(s)?`
    );
    if (!confirmUpgrade) {
      return;
    }

    // 追蹤每個請求的結果
    const results = [];
    let successCount = 0;
    let failureCount = 0;

    // 對每個選中的 clientId 執行 fetch
    for (const clientId of numSelected) {
      try {
        console.log(`Attempting to upgrade firmware for clientId: ${clientId}`);
        // API URL 針對單個 clientId
        const response = await fetch(`/api/v1/device/upgrade/${clientId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json', // 雖然此 POST 可能不攜帶 body，但 Content-Type 仍可保留
            'Authorization': `Bearer ${user.token}`,
          },
          // 如果您的後端 API /api/v1/device/upgrade/:clientId 確實需要請求體，
          // 則取消註解並傳遞 body: JSON.stringify({ /* 任何需要的數據 */ }),
          // 否則，如果 clientId 在 URL 中已足夠，則不需要 body。
          // body: JSON.stringify({ clientId }), // 根據您的 API 需求決定是否需要
        });

        if (!response.ok) {
          let errorData = `Failed to upgrade firmware for ${clientId}. Status: ${response.status}.`;
          try {
            // 嘗試解析錯誤回應（如果是 JSON 格式）
            const errorJson = await response.json();
            errorData += ` Message: ${errorJson.message || errorJson.error || JSON.stringify(errorJson)}`;
          } catch (parseError) {
            // 如果錯誤回應不是 JSON，則使用文本
            const errorText = await response.text();
            errorData += ` Response: ${errorText || response.statusText}`;
          }
          throw new Error(errorData);
        }

        const result = await response.json(); // 假設成功時 API 返回 JSON
        console.log(`Upgrade successful for ${clientId}:`, result);
        results.push({ clientId, status: 'success', data: result });
        successCount++;
      } catch (error) {
        console.error(`Error upgrading firmware for ${clientId}:`, error.message);
        results.push({ clientId, status: 'error', message: error.message });
        failureCount++;
      }
    }

    // 報告結果
    if (failureCount > 0) {
      alert(
        `Firmware upgrade process completed.\nSuccessful: ${successCount}\nFailed: ${failureCount}\n\nPlease check the console for details on failed upgrades.`
      );
    } else {
      alert(`Successfully upgraded firmware for all ${successCount} selected device(s).`);
    }
    console.log('All upgrade results:', results);
    // 在這裡可以根據需要進一步處理 results，例如更新 UI 以反映每個設備的狀態
  };

  return (
    <Toolbar
      sx={[
        {
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        },
        numSelected.length > 0 && { // 修改: 使用 numSelected.length
          bgcolor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        },
      ]}
    >
      {numSelected.length > 0 ? ( // 修改: 使用 numSelected.length
        <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected.length} selected {/* 修改: 顯示 numSelected.length */}
        </Typography>
      ) : (
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Device List
        </Typography>
      )}
      {numSelected.length > 0 ? ( // 修改: 使用 numSelected.length
        <Tooltip title="Upgrade Firmware">
          <IconButton onClick={handleUpgradeFirmware}>
            <BrowserUpdatedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.array.isRequired, // propTypes 正確定義為 array
};

export default function EnhancedTable() {
  const [order, setOrder] = React.useState('desc'); // Default order to 'desc'
  const [orderBy, setOrderBy] = React.useState('status'); // Default orderBy to 'status'
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25); // Default rowsPerPage to 25
  const [rows, setRows] = React.useState([]); // 新增 rows 狀態
  const socketRef = React.useRef(null); // 用於存儲 socket 實例
  const { user } = useAuthContext();

  React.useEffect(() => {
    const port = process.env.REACT_APP_WSPORT;
    const backendUrl = process.env.REACT_APP_SOCKET_URL || window.location.origin;
    const socket = io(backendUrl, { auth: { token: user?.token } });
    socketRef.current = socket;

    // Listen for device status changes
    socket.on("device_status_change", (data) => {
      setRows((prevDevices) => {
        // Check if the device already exists in the list
        const existingDeviceIndex = prevDevices.findIndex(
          (device) => device.clientId === data.clientId
        );

        if (existingDeviceIndex !== -1) {
          // Update the existing device's status
          const updatedDevices = [...prevDevices];
          updatedDevices[existingDeviceIndex] = {
            ...updatedDevices[existingDeviceIndex],
            status: data.status,
            updatedAt: data.timestamp,
            ...(data.version && { version: data.version }),
          };
          return updatedDevices;
        } else {
          // Add the new device to the list
          return [...prevDevices, data];
        }
      });
    });

    // Fetch data from the API
    const fetchData = async () => {
      try {
        const response = await fetch('/api/v1/device/list', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user.token}`,
        }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        const data = await response.json();
        // 假設 API 返回的資料格式為 [{ clientId, status, updatedAt, createdAt }]
        setRows(data);
      } catch (error) {
        console.error('Error fetching device list:', error);
      }
    };

    fetchData();

    // 清理函數
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect(); // 斷開 socket 連接
        console.log("Socket disconnected");
      }
    };
  }, []); // 空依賴陣列表示只在組件掛載和卸載時執行

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.clientId);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, clientId) => {
    const selectedIndex = selected.indexOf(clientId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, clientId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...rows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, rows],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <EnhancedTableToolbar numSelected={selected} />
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size="medium" // 固定為 medium
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = selected.includes(row.clientId);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                  hover
                  onClick={(event) => handleClick(event, row.clientId)}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={row.clientId}
                  selected={isItemSelected}
                  sx={{ cursor: 'pointer' }}
                  >
                  <TableCell padding="checkbox">
                    <Checkbox
                    color="primary"
                    checked={isItemSelected}
                    inputProps={{
                      'aria-labelledby': labelId,
                    }}
                    />
                  </TableCell>
                  <TableCell
                    component="th"
                    id={labelId}
                    scope="row"
                    padding="none"
                  >
                    {row.clientId}
                  </TableCell>
                  <TableCell align="left" padding="none">{row.clientName}</TableCell>
                  <TableCell align="left" sx={{ color: row.status === 'online' ? 'green' : 'red' }}>
                    {row.status}
                  </TableCell>
                  <TableCell align="left" padding="none">{row.version}</TableCell>
                  <TableCell align="left">{row.updatedAt}</TableCell>
                  <TableCell align="left">{row.createdAt}</TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} /> 
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]} // Added 50 and ensured 25 is present
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
